import beeIcon from "@/assets/images/decor/BeeIcon.svg";
import otherIconWhite from "@/assets/images/decor/OtherIconWhite.svg";
import wordIconWhite from "@/assets/images/decor/WordIconWhite.svg";
import { LiveMobileViewContainer } from "@/components/slides/editor/LiveMobileViewContainer";
import { FindOrGenerateVocabCardDocument } from "@/generated/graphql";
import { useAppSelector } from "@/store/hooks";
import type { ReadingSlideData, SubQuiz } from "@ai-teach/types";
import { useLazyQuery } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import { FaPause } from "react-icons/fa";
import { HiVolumeUp } from "react-icons/hi";
import {
  IoArrowForward,
  IoCheckmarkCircleOutline,
  IoChevronBack,
  IoChevronForward,
  IoClose,
  IoCloseCircleOutline,
  IoMic,
  IoReturnDownForwardOutline,
} from "react-icons/io5";

const SAMPLE_TEXT =
  "Baseball is a popular sport in the United States, and many families enjoy going to the stadium together to watch games.";

type Mode = "reading" | "learning";

interface VocabCardData {
  word: string;
  definition: string;
  example: string;
  partOfSpeech: string;
  imageUrl?: string | null;
}

/** Learning-mode word popup — matches mobile VocabCardModal (white card, #2626A5 word, grey rule). */
function VocabPopup({
  card,
  loading,
  error,
  onClose,
}: {
  card: VocabCardData | null;
  loading: boolean;
  error: boolean;
  onClose: () => void;
}) {
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/40 px-6">
      <div className="relative w-full max-w-[320px] rounded-2xl bg-white p-5">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3"
          style={{ color: "#AFAFAF" }}
          aria-label="Close"
        >
          <IoClose size={22} />
        </button>
        {loading ? (
          <p className="py-10 text-center text-sm" style={{ color: "#AFAFAF" }}>
            Loading…
          </p>
        ) : error || !card ? (
          <p className="py-10 text-center text-sm" style={{ color: "#AFAFAF" }}>
            Couldn't load this word.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2 pr-6">
              <HiVolumeUp style={{ color: "#DFDFDF" }} fontSize={32} className="shrink-0" />
              <span className="text-[25px] font-bold" style={{ color: "#2626A5" }}>
                {card.word}
              </span>
              {card.partOfSpeech && (
                <span className="text-[13px]" style={{ color: "#AFAFAF" }}>
                  {card.partOfSpeech}
                </span>
              )}
            </div>
            {card.imageUrl && (
              <img
                src={card.imageUrl}
                alt=""
                className="mt-3 max-h-40 w-full rounded-xl object-cover"
              />
            )}
            <p className="mt-3 text-base leading-6 text-black">{card.definition}</p>
            {card.example && (
              <>
                <p
                  className="mt-4 text-[13px] uppercase tracking-wide"
                  style={{ color: "#AFAFAF" }}
                >
                  Example
                </p>
                <p className="mt-1 border-l-[3px] border-[#E5E5E5] pl-3 text-base leading-6 text-black">
                  {card.example}
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Static port of mobile SnailMascot (no animation) for the reading→quiz bridge. */
function SnailStatic({ size = 140 }: { size?: number }) {
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: decorative mascot
    <svg width={size} height={size} viewBox="0 0 148 149" fill="none">
      <g clipPath="url(#snailClip)">
        <path
          d="M73.6617 148.073C114.34 148.073 147.317 115.085 147.317 74.393C147.317 33.7006 114.34 0.712891 73.6617 0.712891C32.9832 0.712891 0.00683594 33.7006 0.00683594 74.393C0.00683594 115.085 32.9832 148.073 73.6617 148.073Z"
          fill="#0BA13A"
        />
        <mask id="snailMask" maskUnits="userSpaceOnUse" x="0" y="0" width="148" height="149">
          <path
            d="M73.6617 148.073C114.34 148.073 147.317 115.085 147.317 74.393C147.317 33.7006 114.34 0.712891 73.6617 0.712891C32.9832 0.712891 0.00683594 33.7006 0.00683594 74.393C0.00683594 115.085 32.9832 148.073 73.6617 148.073Z"
            fill="white"
          />
        </mask>
        <g mask="url(#snailMask)">
          <path
            d="M57.5721 116.204C57.5721 116.204 63.6631 122.551 53.5159 142.219C43.3686 161.886 64.4006 163.696 68.884 164.551C73.3675 165.406 147.517 167.779 147.517 167.779L299.544 169.691C299.544 169.691 326.867 169.171 308.819 151.609C308.819 151.609 301.356 143.42 276.484 134.58C251.614 125.741 203.533 136.254 168.075 111.103C151.079 99.0491 130.786 99.535 122.298 96.1251C113.516 92.5983 111.587 76.2127 106.746 70.1218C73.2168 27.9263 57.5721 116.204 57.5721 116.204Z"
            fill="#C2C5EE"
          />
          <path
            d="M110.946 82.6501C111.314 61.7456 94.2716 44.4934 72.8802 44.1162C51.4888 43.7389 33.849 60.3795 33.4805 81.2839C33.1121 102.188 50.1546 119.441 71.5461 119.818C92.9375 120.195 110.577 103.555 110.946 82.6501Z"
            fill="#C2C5EE"
          />
          <path
            d="M32.922 22.4314C35.9282 24.449 38.8152 26.6127 41.547 29.0531C44.2519 31.516 46.7162 34.3118 48.8388 37.4067C50.9748 40.4949 52.7196 43.8913 54.1429 47.4495C54.8332 49.2354 55.438 51.0618 55.9596 52.9197C56.4858 54.7775 56.8927 56.6646 57.2795 58.572L44.9265 59.141C45.1176 57.6205 45.2368 56.0821 45.239 54.5459C45.2278 53.0119 45.1558 51.4667 44.9512 49.9372C44.5555 46.876 43.764 43.835 42.5274 40.9358C41.2997 38.0343 39.6628 35.2587 37.6617 32.7216C36.6656 31.4485 35.5707 30.2407 34.3745 29.1363C33.176 28.0365 31.8809 27.0378 30.5049 26.1786L32.922 22.4336V22.4314Z"
            fill="#C2C5EE"
          />
          <path
            d="M29.6171 28.4909C27.3821 27.2268 26.5952 24.3883 27.8588 22.1526C29.1224 19.9169 31.96 19.1296 34.195 20.3937C36.4299 21.6578 37.2169 24.4963 35.9533 26.732C34.6896 28.9677 31.8521 29.755 29.6171 28.4909Z"
            fill="#C2C5EE"
          />
          <path
            d="M56.9386 15.6682C59.9448 17.6858 62.8318 19.8495 65.5636 22.2899C68.2685 24.7528 70.7328 27.5486 72.8554 30.6436C74.9914 33.7318 76.7362 37.1281 78.1595 40.6864C78.8498 42.4722 79.4546 44.2986 79.9762 46.1565C80.5024 48.0143 80.9093 49.9014 81.2961 51.8088L68.9431 52.3778C69.1342 50.8574 69.2534 49.3189 69.2556 47.7827C69.2444 46.2487 69.1724 44.7035 68.9678 43.174C68.5721 40.1128 67.7784 37.0718 66.544 34.1726C65.3163 31.2711 63.6794 28.4956 61.6783 25.9584C60.6822 24.6854 59.5873 23.4775 58.3911 22.3732C57.1926 21.2733 55.8975 20.2746 54.5215 19.4154L56.9386 15.6682Z"
            fill="#C2C5EE"
          />
          <path
            d="M53.6308 21.7253C51.3958 20.4612 50.6089 17.6227 51.8725 15.387C53.1361 13.1512 55.9737 12.364 58.2086 13.6281C60.4436 14.8921 61.2306 17.7307 59.9669 19.9664C58.7033 22.2021 55.8657 22.9893 53.6308 21.7253Z"
            fill="#C2C5EE"
          />
          <path
            d="M308.662 71.5321C304.531 112.616 281.615 154.342 243.088 148.833C240.408 148.451 219.684 135.923 207.373 135.705C184.282 135.298 170.022 131.456 153.606 117.682C131.951 99.5124 114.195 98.5813 114.733 68.1087C115.677 14.5389 159.855 -28.1222 213.408 -27.1775C266.96 -26.2328 314.02 18.2209 308.662 71.5321Z"
            fill="#C2A4DB"
          />
          <path
            d="M74.6431 71.658C75.5065 76.4601 77.71 79.3392 82.0135 78.5902C86.398 77.8277 87.3693 74.3009 86.5239 69.591C85.6785 64.8811 83.3221 61.9346 79.1535 62.6589C75.122 63.3606 73.7887 66.9009 74.6431 71.658Z"
            fill="#FCFFF4"
          />
          <path
            d="M88.857 71.2152C89.6822 74.9152 88.3287 77.6525 84.902 78.5072C81.6822 79.3102 79.0785 76.8271 78.3635 73.4037C77.6485 69.9804 78.4759 67.0992 82.024 66.1455C84.9267 65.365 88.0948 67.8032 88.8548 71.2175L88.857 71.2152Z"
            fill="black"
          />
          <path
            d="M51.5151 75.7381C52.3785 80.5402 54.582 83.4192 58.8856 82.6702C63.2701 81.9077 64.2414 78.381 63.396 73.6711C62.5506 68.9612 60.1942 66.0147 56.0255 66.739C51.9941 67.4407 50.6607 70.981 51.5151 75.7381Z"
            fill="#FCFFF4"
          />
          <path
            d="M65.7292 75.2953C66.5544 78.9953 65.2008 81.7326 61.7742 82.5873C58.5544 83.3903 55.9507 80.9071 55.2357 77.4838C54.5229 74.0605 55.3481 71.1792 58.8961 70.2256C61.7989 69.4451 64.967 71.8832 65.7269 75.2976L65.7292 75.2953Z"
            fill="black"
          />
          <path
            d="M63.6808 86.8572L79.0342 84.0127C79.5621 83.9149 80.0376 84.3635 79.9988 84.9262C79.8396 87.2813 78.8071 92.7009 72.7306 93.8285C66.6463 94.9561 63.9603 90.2113 63.0695 88.0561C62.858 87.5423 63.1568 86.9571 63.6828 86.8592L63.6808 86.8572Z"
            fill="black"
            stroke="black"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </g>
      </g>
      <defs>
        <clipPath id="snailClip">
          <rect width="148" height="149" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

/** Reading → quiz hand-off — matches mobile SubQuizBridge (static snail, no animation). */
function ReadingBridge({ quizCount, onStart }: { quizCount: number; onStart: () => void }) {
  return (
    <div
      className="flex-1 w-full flex flex-col items-center justify-center pb-6"
      style={{ backgroundColor: "#FFE89F" }}
    >
      <div
        className="self-stretch flex flex-col items-center gap-1.5 px-6 py-3.5"
        style={{ backgroundColor: "#FFFFF7", boxShadow: "0 4px 4px rgba(0,0,0,0.25)" }}
      >
        <p className="text-center text-[25px] font-bold" style={{ color: "#4B4B4B" }}>
          Reading Complete
        </p>
        <p className="text-center text-base font-bold text-black">
          {quizCount} quick questions coming up next
        </p>
      </div>

      <div className="my-8">
        <SnailStatic size={140} />
      </div>

      <div className="self-stretch flex justify-center px-5">
        <button
          type="button"
          onClick={onStart}
          className="flex h-12 w-full max-w-[300px] items-center justify-center gap-2.5 rounded-[20px] text-xl font-extrabold text-white"
          style={{ background: "linear-gradient(to right, #3697C7, #00CAD1)" }}
        >
          Start quiz <IoArrowForward size={22} />
        </button>
      </div>
    </div>
  );
}

/** Post-reading mini-quiz — matches mobile SubQuizFlow (bee counter, 31px question, option cards). */
function SubQuizPreview({ quizzes }: { quizzes: SubQuiz[] }) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, { choice: string; correct: boolean }>>({});
  const [textVal, setTextVal] = useState("");
  const timerRef = useRef<number | null>(null);
  useEffect(() => () => window.clearTimeout(timerRef.current ?? undefined), []);

  const quiz = quizzes[index];
  const answered = answers[index];
  const isLast = index >= quizzes.length - 1;
  const canGoBack = index > 0;
  const canGoForward = !!answered && !isLast;
  if (!quiz) return null;

  const norm = (s: string) => s.trim().toLowerCase();
  const goTo = (i: number) => {
    window.clearTimeout(timerRef.current ?? undefined);
    setIndex(i);
  };

  const grade = (choice: string) => {
    if (answered) return;
    setAnswers((p) => ({
      ...p,
      [index]: { choice, correct: norm(choice) === norm(quiz.correctAnswer) },
    }));
    setTextVal("");
    if (!isLast) timerRef.current = window.setTimeout(() => setIndex((i) => i + 1), 1200);
  };

  const isGrid = quiz.options.length > 3;
  const stepBtn = "w-10 h-10 rounded-full flex items-center justify-center border-2 bg-white";

  return (
    <div className="flex-1 w-full flex flex-col px-5">
      {/* Header — bee talking to a yellow speech-bubble counter, step arrows opposite. */}
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-1">
          <img src={beeIcon} alt="" className="h-11 w-9" />
          <div
            className="relative rounded-full px-5 py-[5px] top-[7px] left-[10px]"
            style={{ backgroundColor: "#FFD961" }}
          >
            <span
              className="absolute left-[-6px] top-1/2 -mt-[5px] h-0 w-0"
              style={{
                borderTop: "5px solid transparent",
                borderBottom: "5px solid transparent",
                borderRight: "7px solid #FFD961",
              }}
            />
            <span className="text-base font-black text-black">
              {index + 1} / {quizzes.length}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => canGoBack && goTo(index - 1)}
            disabled={!canGoBack}
            className={stepBtn}
            style={{ borderColor: canGoBack ? "#DDDDDD" : "#00000014" }}
          >
            <IoChevronBack size={22} color={canGoBack ? "#111827" : "#B0B0B0"} />
          </button>
          <button
            type="button"
            onClick={() => canGoForward && goTo(index + 1)}
            disabled={!canGoForward}
            className={stepBtn}
            style={{ borderColor: canGoForward ? "#DDDDDD" : "#00000014" }}
          >
            <IoChevronForward size={22} color={canGoForward ? "#111827" : "#B0B0B0"} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <p
          className="text-center font-bold text-black"
          style={{ fontSize: 31, lineHeight: "40px", marginTop: 32, marginBottom: 24 }}
        >
          {quiz.question}
        </p>

        {quiz.questionMode === "multiple_choice" ? (
          <div className={isGrid ? "flex flex-wrap gap-2" : "flex flex-col gap-4"}>
            {quiz.options.map((opt, i) => {
              const isCorrectOpt = norm(opt) === norm(quiz.correctAnswer);
              const isSuccess = !!answered && isCorrectOpt;
              const isFail = !!answered && answered.choice === opt && !isCorrectOpt;
              const isDisabled = !!answered && !isSuccess && !isFail;
              return (
                <button
                  key={`${quiz.id}-o${i + 1}`}
                  type="button"
                  disabled={!!answered}
                  onClick={() => grade(opt)}
                  className={`flex items-center justify-between rounded-[15px] border-2 shadow-[0_2px_4px_rgba(0,0,0,0.08)] ${
                    isGrid
                      ? "w-[48%] min-h-[64px] justify-center px-4 py-4"
                      : "w-full px-2.5 py-[5px]"
                  } ${isDisabled ? "opacity-50" : ""}`}
                  style={{
                    borderColor: isSuccess ? "#34C759" : isFail ? "#FF4C39" : "#DDDDDD",
                    backgroundColor: isSuccess
                      ? "#9DF0B2"
                      : isFail
                        ? "#FFA49A"
                        : isDisabled
                          ? "#f9fafb"
                          : "#FFFFFF",
                  }}
                >
                  <span className="text-2xl" style={{ color: isFail ? "#FF4C39" : "#000000" }}>
                    {opt || `Option ${i + 1}`}
                  </span>
                  {isSuccess && <IoCheckmarkCircleOutline size={22} color="#000000" />}
                  {isFail && <IoCloseCircleOutline size={22} color="#000000" />}
                </button>
              );
            })}
          </div>
        ) : (
          <div>
            <div className="relative">
              <input
                value={answered ? answered.choice : textVal}
                onChange={(e) => setTextVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && textVal.trim()) grade(textVal.trim());
                }}
                disabled={!!answered}
                placeholder="Type your answer..."
                className="w-full rounded-[15px] border-2 py-3 pl-3 pr-12 text-2xl outline-none min-h-[48px]"
                style={{
                  borderColor: answered ? (answered.correct ? "#34C759" : "#FF4C39") : "#DDDDDD",
                  backgroundColor: answered
                    ? answered.correct
                      ? "#9DF0B2"
                      : "#FFA49A"
                    : "#FFFFFF",
                  color: answered && !answered.correct ? "#FF4C39" : "#000000",
                }}
              />
              {answered ? (
                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                  {answered.correct ? (
                    <IoCheckmarkCircleOutline size={24} color="#000000" />
                  ) : (
                    <IoCloseCircleOutline size={24} color="#000000" />
                  )}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => textVal.trim() && grade(textVal.trim())}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: textVal.trim() ? "#3A86FF" : "#AFAFAF" }}
                  aria-label="Submit answer"
                >
                  <IoReturnDownForwardOutline size={26} />
                </button>
              )}
            </div>
            {answered && !answered.correct && (
              <p className="mt-2 text-center font-bold" style={{ color: "#34C759", fontSize: 18 }}>
                Answer: {quiz.correctAnswer}
              </p>
            )}
          </div>
        )}
      </div>

      {isLast && answered && (
        <div className="flex w-full justify-center pt-3 pb-[72px]">
          <div
            className="flex h-12 w-full max-w-[260px] items-center justify-center gap-2 rounded-[20px] font-bold text-white"
            style={{
              background: "linear-gradient(to right, #3697C7, #00CAD1)",
              fontSize: 20,
              letterSpacing: 0.5,
            }}
          >
            See results <IoArrowForward size={20} />
          </div>
        </div>
      )}
    </div>
  );
}

/** Speaker (plays the passage audio) | progress | score. Play-recording lives in the header now.
 *  Video passages have no speaker — the clip is the model read (PassageCard.tsx). */
function PassageControlBar({
  audioSrc,
  showSpeaker,
}: { audioSrc?: string | null | undefined; showSpeaker: boolean }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setProgress(el.duration ? el.currentTime / el.duration : 0);
    const onEnd = () => {
      setPlaying(false);
      setProgress(0);
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      void el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="flex w-full items-center gap-2">
      {audioSrc && (
        // biome-ignore lint/a11y/useMediaCaption: preview-only playback of the authored passage audio
        <audio ref={audioRef} src={audioSrc} preload="metadata" />
      )}
      {showSpeaker && (
        <button
          type="button"
          onClick={toggle}
          disabled={!audioSrc}
          className="shrink-0 disabled:opacity-60"
          style={{ color: "#AFAFAF" }}
          aria-label={playing ? "Pause" : "Play passage audio"}
        >
          {playing ? <FaPause fontSize={16} /> : <HiVolumeUp fontSize={22} />}
        </button>
      )}
      {/* Progress bar only appears once playback starts, matching mobile PassageCard. */}
      {playing && (
        <div
          className="h-1 rounded-sm overflow-hidden"
          style={{ width: "40%", backgroundColor: "rgba(175, 175, 175, 0.3)" }}
        >
          <div
            className="h-full rounded-sm transition-[width] duration-150"
            style={{ width: `${Math.round(progress * 100)}%`, backgroundColor: "#2626A5" }}
          />
        </div>
      )}
      <div className="ml-auto flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "#999999" }} />
        <span className="text-sm font-bold" style={{ color: "#2626A5" }}>
          --/100
        </span>
      </div>
    </div>
  );
}

/** Passage card — borderless #FFFFF7, edge-to-edge; non-current passages dimmed (PassageCard.tsx). */
function PassageCard({
  cardKey,
  text,
  imageSrc,
  videoSrc,
  audioSrc,
  dimmed,
  onWordClick,
  activeKey,
}: {
  cardKey: string;
  text: string;
  imageSrc?: string | null | undefined;
  videoSrc?: string | null | undefined;
  audioSrc?: string | null | undefined;
  dimmed?: boolean;
  /** Reading: play the word. Learning: open its card. Omitted for dimmed (non-current) cards. */
  onWordClick?: (word: string, key: string) => void;
  /** Unique key of the word currently highlighted (per instance, not per string). */
  activeKey?: string | null;
}) {
  const body = text || SAMPLE_TEXT;
  return (
    <div
      className={`overflow-hidden ${dimmed ? "opacity-40" : ""}`}
      style={{ backgroundColor: "#FFFFF7" }}
    >
      {/* Order matches PassageCard.tsx: one centred visual above the control bar, then the bar,
          then the text. Widths mirror its IMAGE_WIDTH / VIDEO_WIDTH. */}
      {imageSrc && (
        <div className="w-[60%] max-w-[480px] mx-auto aspect-square rounded-[20px] overflow-hidden bg-black">
          <img src={imageSrc} alt="passage" className="w-full h-full object-cover" />
        </div>
      )}
      {videoSrc && (
        <div className="w-[85%] max-w-[480px] mx-auto aspect-video rounded-[20px] overflow-hidden bg-black">
          {/* biome-ignore lint/a11y/useMediaCaption: preview of the student-facing passage video */}
          <video src={videoSrc} controls className="w-full h-full object-contain" />
        </div>
      )}
      <div className="px-4 pt-4">
        <PassageControlBar audioSrc={audioSrc} showSpeaker={!videoSrc} />
      </div>
      <div className="px-5 py-4">
        {/* Colour matches PassageCard.tsx's passageText (#AFAFAF, unscored base). */}
        <p className="text-[21px] font-semibold leading-[1.6]" style={{ color: "#AFAFAF" }}>
          {onWordClick
            ? body.split(/(\s+)/).map((tok, i) => {
                if (/^\s+$/.test(tok) || tok === "") return tok;
                const key = `${cardKey}-${i}`;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onWordClick(tok, key)}
                    className="inline cursor-pointer border-0 bg-transparent p-0 align-baseline font-[inherit] leading-[inherit] transition-colors hover:text-[#2626A5]"
                    style={activeKey === key ? { color: "#2626A5" } : undefined}
                  >
                    {tok}
                  </button>
                );
              })
            : body}
        </p>
      </div>
    </div>
  );
}

export function ReadingFluencyLiveMobileView() {
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const [mode, setMode] = useState<Mode>("reading");
  const [phase, setPhase] = useState<"reading" | "bridge" | "quiz">("reading");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [recorded, setRecorded] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const wordAudioRef = useRef<HTMLAudioElement | null>(null);
  const [findVocabCard] = useLazyQuery(FindOrGenerateVocabCardDocument, {
    fetchPolicy: "cache-first",
  });

  const [vocabOpen, setVocabOpen] = useState(false);
  const [vocabCard, setVocabCard] = useState<VocabCardData | null>(null);
  const [vocabLoading, setVocabLoading] = useState(false);
  const [vocabError, setVocabError] = useState(false);

  const cleanWord = (token: string) => token.replace(/[^A-Za-z'-]/g, "");

  const playWord = async (token: string, key: string) => {
    const word = cleanWord(token);
    if (!word) return;
    setActiveKey(key);
    try {
      const res = await findVocabCard({ variables: { word } });
      const card = res.data?.findOrGenerateVocabCard as { audioUrl?: string } | undefined;
      if (card?.audioUrl && wordAudioRef.current) {
        wordAudioRef.current.src = card.audioUrl;
        void wordAudioRef.current.play();
      } else {
        setActiveKey(null);
      }
    } catch {
      setActiveKey(null);
    }
  };

  const openVocab = async (token: string) => {
    const word = cleanWord(token);
    if (!word) return;
    setVocabOpen(true);
    setVocabCard(null);
    setVocabError(false);
    setVocabLoading(true);
    try {
      const res = await findVocabCard({ variables: { word } });
      const card = (res.data?.findOrGenerateVocabCard as VocabCardData | undefined) ?? null;
      setVocabCard(card);
      if (!card) setVocabError(true);
    } catch {
      setVocabError(true);
    } finally {
      setVocabLoading(false);
    }
  };

  const handleWord = mode === "reading" ? playWord : (token: string) => openVocab(token);

  if (!slide) return null;

  const readingSlideData = slide.slideData as ReadingSlideData;
  const passages = readingSlideData.passages ?? [];
  const subQuizzes = readingSlideData.subQuizzes ?? [];
  const onLastPassage = currentIndex >= passages.length - 1;
  const canGoNext = !onLastPassage || subQuizzes.length > 0;

  const handleNext = () => {
    setRecorded(false);
    if (!onLastPassage) {
      setCurrentIndex((i) => i + 1);
      return;
    }
    if (subQuizzes.length > 0) setPhase("bridge");
  };

  if (phase === "bridge") {
    return (
      <LiveMobileViewContainer className="relative">
        <ReadingBridge quizCount={subQuizzes.length} onStart={() => setPhase("quiz")} />
      </LiveMobileViewContainer>
    );
  }

  if (phase === "quiz") {
    return (
      <LiveMobileViewContainer className="relative">
        <SubQuizPreview quizzes={subQuizzes} />
      </LiveMobileViewContainer>
    );
  }

  return (
    <LiveMobileViewContainer className="relative">
      {/* Header row — matches Reading.tsx's modeWrapper: mode pill, then the book/pencil toggle */}
      <div className="-mt-1 w-full flex items-center justify-between px-4">
        <div
          className="bg-white rounded-full px-4 py-2 border shrink-0"
          style={{ borderColor: "#EEEEEE" }}
        >
          <span className="font-bold text-xl" style={{ color: "#727171" }}>
            {mode === "reading" ? "Reading" : "Learning"} mode
          </span>
        </div>

        {/* Reading/Learning toggle — matches Reading.tsx's modeToggle: grey pill, 3px white
            border, the active option a coloured circle, the inactive one bare on the grey. */}
        <div
          className="flex items-center rounded-full border-[3px] border-white bg-[#E5E7EB] p-1 gap-1 shrink-0"
          style={{ boxShadow: "0 2px 6px rgba(170,170,170,0.4)" }}
        >
          <button
            type="button"
            onClick={() => setMode("reading")}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={mode === "reading" ? { backgroundColor: "#FFAC27" } : undefined}
          >
            <img src={wordIconWhite} alt="" className="w-[26px]" />
          </button>
          <button
            type="button"
            onClick={() => setMode("learning")}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={mode === "learning" ? { backgroundColor: "#97D100" } : undefined}
          >
            <img src={otherIconWhite} alt="" className="w-[19px] mt-0.5 ml-1" />
          </button>
        </div>
      </div>

      {/* Passage content — full-bleed cards, cream page bg from LiveMobileViewContainer */}
      <div className="flex-1 w-full min-h-0 overflow-y-auto flex flex-col gap-3">
        {passages.length > 0 ? (
          passages.map((passage, index) => (
            <PassageCard
              key={`passage-${index + 1}`}
              cardKey={`p${index}`}
              text={passage?.text ?? ""}
              imageSrc={
                readingSlideData.isTextOnly || passage?.mediaType === "video"
                  ? null
                  : passage?.image?.src
              }
              videoSrc={
                readingSlideData.isTextOnly || passage?.mediaType !== "video"
                  ? null
                  : passage?.video?.src
              }
              audioSrc={
                !readingSlideData.isTextOnly && passage?.mediaType === "video"
                  ? null
                  : passage?.audio?.src
              }
              dimmed={mode === "reading" && index !== currentIndex}
              {...((mode === "learning" || index === currentIndex) && {
                onWordClick: handleWord,
                activeKey,
              })}
            />
          ))
        ) : (
          <PassageCard cardKey="p0" text="" onWordClick={handleWord} activeKey={activeKey} />
        )}
      </div>

      {/* biome-ignore lint/a11y/useMediaCaption: preview-only per-word pronunciation playback */}
      <audio ref={wordAudioRef} onEnded={() => setActiveKey(null)} />

      {/* Bottom bar — reading only. Mic first; once "recorded" it becomes Next, which walks the
          passages then opens the sub-quiz bridge (disabled on the last passage if there's none). */}
      {mode === "reading" && (
        <div
          className="w-full flex justify-center pt-4 pb-6 rounded-t-3xl"
          style={{ backgroundColor: "#FFF7DD" }}
        >
          {recorded ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canGoNext}
              className="flex h-12 w-full max-w-[260px] items-center justify-center gap-2 rounded-[20px] text-xl font-bold text-white disabled:opacity-40"
              style={{ background: "linear-gradient(to right, #3697C7, #00CAD1)" }}
            >
              Next <IoArrowForward size={20} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setRecorded(true)}
              className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
              style={{ background: "linear-gradient(180deg, #FF8500 0%, #FFB800 100%)" }}
            >
              <IoMic className="text-3xl p-2" color="white" />
            </button>
          )}
        </div>
      )}

      {vocabOpen && (
        <VocabPopup
          card={vocabCard}
          loading={vocabLoading}
          error={vocabError}
          onClose={() => setVocabOpen(false)}
        />
      )}
    </LiveMobileViewContainer>
  );
}
